'use client';

import { useState } from 'react';
import Link from 'next/link';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, MessageCircle, Send } from 'lucide-react';
import DiagonalSeparator from '@/components/DiagonalSeparator';
import LocationMap from '@/components/LocationMap';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { trackEvent } from '@/lib/analytics';
import { appSettings } from '@/app-settings';

// ... (existing imports)

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [hasInitiated, setHasInitiated] = useState(false);

  const handleInitiate = () => {
    if (!hasInitiated) {
      trackEvent('contact_initiate', { source_page: 'contact_page' });
      setHasInitiated(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    trackEvent('contact_submit', {
      subject_category: formData.subject, // Assuming subject is somewhat categorical, or we could categorize it
    });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      // Add artificial delay for better UX
      await new Promise(resolve => setTimeout(resolve, 3000));

      if (response.ok) {
        setSubmitStatus('success');
        trackEvent('contact_success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
        trackEvent('contact_error', { error_type: 'api_error' });
      }
    } catch {
      setSubmitStatus('error');
      trackEvent('contact_error', { error_type: 'network_error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Layered background */}
        <div className="absolute inset-0 bg-background" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,hsl(var(--primary)/0.15),transparent)]" />

        {/* Animated grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />

        {/* Floating decorative elements */}
        <div className="absolute top-20 left-[10%] w-64 h-64 rounded-full bg-primary/5 blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-[15%] w-48 h-48 rounded-full bg-primary/10 blur-2xl" />

        {/* Decorative shapes */}
        <div className="absolute top-32 right-[20%] w-3 h-3 rounded-full bg-primary/60" />
        <div className="absolute top-40 right-[25%] w-2 h-2 rounded-full bg-white/30" />
        <div className="absolute bottom-20 left-[18%] w-2 h-2 rounded-full bg-primary/40" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center">
            {/* Badge */}
            <Badge variant="outline" className="gap-2 mb-6 border-primary/20 bg-primary/10 text-primary">
              <MessageCircle className="w-4 h-4" />
              <span>Quick response guaranteed</span>
            </Badge>

            <h1 className="text-5xl lg:text-6xl font-display font-bold text-foreground mb-4 tracking-tight">
              Get in{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-red-400 to-orange-400">
                Touch
              </span>
            </h1>

            <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
              Questions about vehicles, routes, or bookings? Drop us a line anytime — we typically respond within the hour.
            </p>

            {/* Quick contact pills */}
            <div className="flex flex-wrap gap-3 justify-center">
              <a href={`tel:+${appSettings.contactPhoneCountry}${appSettings.contactPhoneNumber}`}>
                <Badge variant="secondary" className="gap-2 hover:bg-white/10 transition-colors cursor-pointer">
                  <Phone className="w-4 h-4" />
                  +{appSettings.contactPhoneCountry} {appSettings.contactPhoneNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}
                </Badge>
              </a>
              <a href={`mailto:${appSettings.contactEmail}`}>
                <Badge variant="secondary" className="gap-2 hover:bg-white/10 transition-colors cursor-pointer">
                  <Mail className="w-4 h-4" />
                  Email us
                </Badge>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 bg-card">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2 text-card-foreground">Address</h3>
                    <p className="text-muted-foreground">
                      43/12, St. Joseph Street,<br />
                      Bandaranayaka Square,<br />
                      Negombo. 11500.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2 text-card-foreground">WhatsApp / Viber</h3>
                    <p className="text-muted-foreground">+{appSettings.contactPhoneCountry}{appSettings.contactPhoneNumber.replace(/(\d{3})(\d{3})(\d{3})/, ' $1 $2 $3')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2 text-card-foreground">Email</h3>
                    <p className="text-muted-foreground">{appSettings.contactEmail}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2 text-card-foreground">Working Hours</h3>
                    <p className="text-muted-foreground">24/7 Customer Service</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4 pt-2">
              <Button variant="secondary" size="icon-lg" asChild>
                <Link
                  href="https://www.facebook.com/srilankabikerent"
                  target="_blank"
                  className="hover:bg-[#1877f2] hover:text-white"
                >
                  <Facebook size={24} />
                </Link>
              </Button>
              <Button variant="instagram" size="icon-lg" asChild>
                <Link
                  href="https://www.instagram.com/srilanka.bikerent"
                  target="_blank"
                >
                  <Instagram size={24} />
                </Link>
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <Card variant="elevated">
            <CardContent className="p-8">

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    autoComplete="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    onFocus={handleInitiate}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    autoComplete="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    name="subject"
                    autoComplete="off"
                    type="text"
                    placeholder="How can we help?"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    autoComplete="off"
                    rows={5}
                    placeholder="Your message..."
                    className="resize-none"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full gap-2" size="lg" disabled={isSubmitting}>
                  <Send className="w-4 h-4" />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
                {submitStatus === 'success' && (
                  <p className="text-green-500 text-sm text-center mt-2">Message sent successfully!</p>
                )}
                {submitStatus === 'error' && (
                  <p className="text-red-500 text-sm text-center mt-2">Failed to send message. Please try again.</p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      <DiagonalSeparator
        direction="up"
        fromClass="bg-card"
        toClass="bg-background"
        height={60}
      />

      {/* Map */}
      <section className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
            <div>
              <h3 className="text-2xl font-bold mb-2">Pickup & Drop-off Points</h3>
              <p className="text-muted-foreground text-sm">Island-wide coverage with convenient locations</p>
            </div>
            <Button variant="outline" asChild>
              <Link
                href="https://maps.app.goo.gl/Ze6Lkq45iC1C4vsu6"
                target="_blank"
              >
                <MapPin className="mr-2 h-4 w-4" />
                Open in Google Maps
              </Link>
            </Button>
          </div>
          <Card variant="elevated" className="overflow-hidden p-0">
            <LocationMap className="aspect-video" />
          </Card>
        </div>
      </section>
    </>
  );
}
